import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';
import { callBlockedReason, placeOutboundCall } from '../outboundCall';
import { useOutboundAvailable } from '../useOutboundAvailable';

/** Calls a ticket's customer. Shows nothing for a tenant that can't place calls. */
export function CallCustomerButton({ ticketId, phone }: { ticketId: string; phone: string }) {
  const navigate = useNavigate();
  const isOnline = useVoiceAgentStore((s) => s.isOnline);
  const mediaUrl = useVoiceAgentStore((s) => s.mediaUrl);
  const onCall = useVoiceAgentStore((s) => !!s.activeCall || !!s.incomingCall);
  const available = useOutboundAvailable();
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!available) return null;

  const blocked = callBlockedReason({ phone, isOnline, onCall, mediaUrl });

  const call = async () => {
    // Locked before anything else, so a double click can't reach the server twice.
    setPlacing(true);
    setError(null);
    try {
      await placeOutboundCall({ ticketId, customerPhone: phone });
      navigate('/live-call/active');
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={call}
        disabled={placing || !!blocked}
        title={blocked ?? undefined}
        className="inline-flex items-center gap-1.5 rounded border border-[var(--co-line)] px-2 py-1 text-xs font-medium text-[var(--co-ink)] transition hover:bg-[var(--co-brass-wash)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Phone className="h-3 w-3 flex-shrink-0" />
        {placing ? 'Calling…' : 'Call customer'}
      </button>
      {blocked && <p className="mt-0.5 text-xs text-[var(--co-ink-3)]">{blocked}</p>}
      {error && (
        <p role="alert" className="mt-0.5 text-xs text-[var(--co-terracotta)]">
          {error}
        </p>
      )}
    </div>
  );
}
